import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Pencil, Trash2, Plus, X, AlertTriangle, Sparkles, RefreshCw, ShieldCheck } from "lucide-react";
import PhotoManager from "@/components/admin/PhotoManager";

const LANGS = ["en", "es", "ru", "fr", "de", "it", "nl", "pl"];
const ACTIVE_GENERATION_STAGES = new Set(["QUEUED", "ANALYZING", "GENERATING", "LOCALIZING", "QA", "FINALIZING"]);
const GENERATION_LABELS = { QUEUED: "En cola", ANALYZING: "Analizando", GENERATING: "Generando", LOCALIZING: "Localizando", QA: "Validando", FINALIZING: "Finalizando", READY: "Listo", QA_FAILED: "Error de QA", FAILED: "Error" };
const emptyLangText = () => Object.fromEntries(LANGS.map((lang) => [lang, ""]));
const emptyLocaleStatus = () => Object.fromEntries(LANGS.map((lang) => [lang, "MISSING"]));
const emptyTranslationRevision = () => Object.fromEntries(LANGS.map((lang) => [lang, 0]));
const emptyQaIssues = () => Object.fromEntries(LANGS.map((lang) => [lang, []]));

const blank = {
  name: "", brand: "", model: "",
  categoryKey: "luxury-suv",
  categoryLabel: { ...emptyLangText(), en: "Luxury SUVs", es: "SUV de lujo", ru: "Премиальные SUV" },
  priceDaily: 0, priceWeekly: 0, priceMonthly: 0, deposit: 0,
  includedKmPerDay: 0, extraKmPrice: 0,
  horsepower: 0, topSpeed: 0, acceleration: 0,
  transmission: "Auto", fuelType: "Petrol", seats: 5,
  location: "Benidorm",
  descriptionShort: emptyLangText(),
  descriptionFull: emptyLangText(),
  conditionsText: emptyLangText(),
  images: [],
  available: true, featured: false,
  sourceUrl: "", missingFields: [],
  // SEO (Prompt 9)
  slug: "",
  seoTitle: emptyLangText(),
  seoDescription: emptyLangText(),
  seoDescriptionLong: emptyLangText(),
  faqs: [],
  seoEnabled: true,
  noindex: false,
  contentStatus: emptyLocaleStatus(),
  sourceRevision: 1,
  translationRevision: emptyTranslationRevision(),
  qaIssues: emptyQaIssues(),
  generatedAt: emptyLangText(),
  factsUpdatedAt: "",
  vehicleH1: emptyLangText(),
  commercialCopy: emptyLangText(),
  contentNotes: "",
  generationState: "idle",
  generationError: "",
  generationProgress: 0,
  generationLocalesCompleted: [],
  generationStartedAt: "",
  generationUpdatedAt: "",
  generationStageHistory: [],
};

export default function VehiclesAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeLang, setActiveLang] = useState("en");
  const [aiBusy, setAiBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/vehicles");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.id) await api.put(`/vehicles/${editing.id}`, editing);
    else await api.post("/vehicles", editing);
    setEditing(null); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/vehicles/${id}`); load();
  };

  const waitForGeneration = async (id) => {
    for (let attempt = 0; attempt < 90; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      const { data } = await api.get(`/vehicles/${id}`);
      setEditing((current) => current?.id === id ? { ...current, ...data } : current);
      if (!ACTIVE_GENERATION_STAGES.has(data.generationState)) return data;
    }
    throw new Error("La generación sigue ejecutándose. Puedes cerrar y volver a abrir el vehículo.");
  };

  const generateContent = async (updateAll) => {
    if (!editing?.id || aiBusy) return;
    setAiBusy(true);
    try {
      await api.post(`/vehicles/${editing.id}/generate-content`, {
        sourceLanguage: "en", updateAll, notes: editing.contentNotes || "",
      });
      setEditing((current) => ({ ...current, generationState: "QUEUED", generationProgress: 3, generationLocalesCompleted: [], generationError: "" }));
      const updated = await waitForGeneration(editing.id);
      startEdit(updated);
      await load();
    } catch (error) {
      const message = error?.response?.data?.detail || error.message || "No se pudo generar el contenido";
      setEditing((current) => ({ ...current, generationState: "FAILED", generationProgress: 100, generationError: message }));
    } finally {
      setAiBusy(false);
    }
  };

  const runQa = async () => {
    if (!editing?.id) return;
    const { data } = await api.post(`/vehicles/${editing.id}/qa`);
    setEditing((current) => ({ ...current, contentStatus: data.contentStatus, qaIssues: data.qaIssues }));
    await load();
  };

  const startEdit = (v) => {
    const merged = { ...blank, ...v };
    ["categoryLabel","descriptionShort","descriptionFull","conditionsText","seoTitle","seoDescription","seoDescriptionLong","vehicleH1","commercialCopy"].forEach((k) => {
      merged[k] = { ...blank[k], ...(v[k] || {}) };
    });
    merged.contentStatus = { ...emptyLocaleStatus(), ...(v.contentStatus || {}) };
    merged.translationRevision = { ...emptyTranslationRevision(), ...(v.translationRevision || {}) };
    merged.qaIssues = { ...emptyQaIssues(), ...(v.qaIssues || v.localeQaIssues || {}) };
    merged.generatedAt = { ...emptyLangText(), ...(v.generatedAt || {}) };
    merged.faqs = (Array.isArray(v.faqs) ? v.faqs : []).map((f) => ({
      ...f,
      _uid: f._uid || `faq-${Math.random().toString(36).slice(2, 9)}`,
      q: { ...emptyLangText(), ...(f.q || {}) },
      a: { ...emptyLangText(), ...(f.a || {}) },
    }));
    // legacy string images → objects
    if (Array.isArray(v.images) && v.images.length && typeof v.images[0] === "string") {
      merged.images = v.images.map((u, i) => ({ url: u, alt: emptyLangText(), isMain: i === 0, order: i }));
    } else {
      merged.images = (v.images || []).map((image) => ({
        ...image,
        alt: { ...emptyLangText(), ...(image.alt || {}) },
      }));
    }
    setEditing(merged);
  };

  const addFaq = () =>
    setEditing((prev) => ({
      ...prev,
      faqs: [
        ...(prev.faqs || []),
        { _uid: `faq-${Math.random().toString(36).slice(2, 9)}`, q: emptyLangText(), a: emptyLangText() },
      ],
    }));
  const removeFaq = (i) =>
    setEditing((prev) => ({ ...prev, faqs: (prev.faqs || []).filter((_, idx) => idx !== i) }));
  const setFaq = (i, key, lang, value) =>
    setEditing((prev) => ({
      ...prev,
      faqs: (prev.faqs || []).map((f, idx) =>
        idx === i ? { ...f, [key]: { ...(f[key] || {}), [lang]: value } } : f,
      ),
    }));

  return (
    <div data-testid="admin-vehicles-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-[#F5F1EB] text-3xl">Vehicles</h1>
        <button className="btn-gold" data-testid="admin-vehicle-new" onClick={() => setEditing({ ...JSON.parse(JSON.stringify(blank)) })}>
          <Plus size={14}/> New
        </button>
      </div>

      <div className="grid gap-3">
        {items.map((v) => {
          const img = (v.images || []).find((i) => i.isMain) || (v.images || [])[0];
          const url = typeof img === "string" ? img : img?.url;
          return (
            <div key={v.id} className="border border-[#2A2A2A] p-4 flex gap-4 items-center" data-testid={`admin-vehicle-row-${v.id}`}>
              <img src={url} alt={v.name} className="w-20 h-14 object-cover bg-[#1a1a1a]" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-[#F5F1EB] truncate">{v.name}</div>
                <div className="text-xs text-[#8A8A8A]">€{v.priceDaily}/day · {v.categoryKey} · {v.available ? "Active" : "Hidden"}</div>
                {(v.missingFields?.length || 0) > 0 && (
                  <div className="text-[10px] text-[#B8342A] mt-1 flex items-center gap-1"><AlertTriangle size={10}/> Missing: {v.missingFields.join(", ")}</div>
                )}
              </div>
              <button onClick={() => startEdit(v)} className="p-2 border border-[#2A2A2A] hover:border-[#C9A55A]" data-testid={`admin-vehicle-edit-${v.id}`}><Pencil size={14}/></button>
              <button onClick={() => remove(v.id)} className="p-2 border border-[#2A2A2A] hover:border-[#B8342A] hover:text-[#B8342A]" data-testid={`admin-vehicle-delete-${v.id}`}><Trash2 size={14}/></button>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-[#0C0C0C]/95 z-50 overflow-y-auto" data-testid="admin-vehicle-editor">
          <div className="max-w-3xl mx-auto p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-[#F5F1EB] text-2xl">{editing.id ? "Edit" : "New"} vehicle</h2>
              <button onClick={() => setEditing(null)} className="p-2 border border-[#2A2A2A] text-[#F5F1EB]" data-testid="admin-vehicle-cancel"><X size={16}/></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" value={editing.name} onChange={(v) => setEditing({...editing, name: v})} testid="ve-name" />
              <Field label="Brand" value={editing.brand} onChange={(v) => setEditing({...editing, brand: v})} testid="ve-brand" />
              <Field label="Model" value={editing.model} onChange={(v) => setEditing({...editing, model: v})} testid="ve-model" />
              <Field label="Category key (slug)" value={editing.categoryKey} onChange={(v) => setEditing({...editing, categoryKey: v.toLowerCase().replace(/\s+/g, "-")})} testid="ve-categoryKey" />
              <Field label="Price/day (€)" type="number" value={editing.priceDaily} onChange={(v) => setEditing({...editing, priceDaily: Number(v)})} testid="ve-pricePerDay" />
              <Field label="Price/week (€)" type="number" value={editing.priceWeekly} onChange={(v) => setEditing({...editing, priceWeekly: Number(v)})} testid="ve-priceWeekly" />
              <Field label="Price/month (€)" type="number" value={editing.priceMonthly} onChange={(v) => setEditing({...editing, priceMonthly: Number(v)})} testid="ve-priceMonthly" />
              <Field label="Deposit (€)" type="number" value={editing.deposit} onChange={(v) => setEditing({...editing, deposit: Number(v)})} testid="ve-deposit" />
              <Field label="Horsepower" type="number" value={editing.horsepower} onChange={(v) => setEditing({...editing, horsepower: Number(v)})} testid="ve-hp" />
              <Field label="Top speed (km/h)" type="number" value={editing.topSpeed} onChange={(v) => setEditing({...editing, topSpeed: Number(v)})} testid="ve-topspeed" />
              <Field label="0-100 (s)" type="number" step="0.1" value={editing.acceleration} onChange={(v) => setEditing({...editing, acceleration: Number(v)})} testid="ve-accel" />
              <Field label="Transmission" value={editing.transmission} onChange={(v) => setEditing({...editing, transmission: v})} testid="ve-trans" />
              <Field label="Fuel" value={editing.fuelType} onChange={(v) => setEditing({...editing, fuelType: v})} testid="ve-fuel" />
              <Field label="Seats" type="number" value={editing.seats} onChange={(v) => setEditing({...editing, seats: Number(v)})} testid="ve-seats" />
              <Field label="Location" value={editing.location} onChange={(v) => setEditing({...editing, location: v})} testid="ve-location" />
            </div>

            <div className="mt-6">
              <div className="text-overline text-[#C9A55A] mb-3">Photos · drag/click to upload, reorder, set main</div>
              <PhotoManager images={editing.images} onChange={(images) => setEditing({...editing, images})} testid="ve-photos" />
            </div>

            {editing.id && (
              <div className="mt-6 border border-[#2A2A2A] p-4" data-testid="ve-ai-panel">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <div className="text-overline text-[#C9A55A]">Contenido IA · 8 idiomas</div>
                    <p className="text-[11px] text-[#8A8A8A] mt-1">
                      Usa únicamente los facts centrales y las fotos. Nunca modifica precios, depósito, specs ni disponibilidad.
                    </p>
                  </div>
                  <button type="button" onClick={runQa} disabled={aiBusy}
                    className="px-3 py-2 border border-[#2A2A2A] text-xs text-[#F5F1EB] flex items-center gap-2"
                    data-testid="ve-run-qa"><ShieldCheck size={14}/> QA 8/8</button>
                </div>
                <Field label="Notas para la IA (opcionales)" value={editing.contentNotes || ""}
                  onChange={(v) => setEditing({...editing, contentNotes: v})} textarea testid="ve-ai-notes" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => generateContent(false)} disabled={aiBusy}
                    className="btn-gold flex items-center gap-2 disabled:opacity-50" data-testid="ve-generate-ai">
                    <Sparkles size={14}/>{aiBusy ? "Generando…" : "Generar contenido con IA"}
                  </button>
                  <button type="button" onClick={() => generateContent(true)} disabled={aiBusy}
                    className="px-4 py-3 border border-[#C9A55A] text-[#C9A55A] uppercase text-xs tracking-[0.12em] flex items-center gap-2 disabled:opacity-50"
                    data-testid="ve-update-all-ai"><RefreshCw size={14}/>Actualizar todos los idiomas con IA</button>
                </div>
                {editing.generationError && <p className="text-xs text-[#B8342A] mt-3">{editing.generationError}</p>}
                {editing.generationState && editing.generationState !== "idle" && (
                  <div className="mt-4 border border-[#2A2A2A] p-3" data-testid="ve-generation-progress">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#F5F1EB]">{GENERATION_LABELS[editing.generationState] || editing.generationState}</span>
                      <span className="text-[#C9A55A]">{editing.generationProgress || 0}% · {(editing.generationLocalesCompleted || []).length}/8 idiomas</span>
                    </div>
                    <div className="h-1 bg-[#2A2A2A] mt-2"><div className="h-full bg-[#C9A55A] transition-all" style={{ width: (editing.generationProgress || 0) + "%" }} /></div>
                    <div className="mt-2 text-[10px] text-[#8A8A8A] flex flex-wrap gap-x-4 gap-y-1">
                      {editing.generationStartedAt && <span>Inicio: {new Date(editing.generationStartedAt).toLocaleTimeString()}</span>}
                      {editing.generationUpdatedAt && <span>Última actualización: {new Date(editing.generationUpdatedAt).toLocaleTimeString()}</span>}
                      {ACTIVE_GENERATION_STAGES.has(editing.generationState) && <span>La generación continúa en segundo plano.</span>}
                    </div>
                  </div>
                )}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {LANGS.map((lang) => {
                    const status = editing.contentStatus?.[lang] || "MISSING";
                    const color = status === "READY" ? "text-emerald-400 border-emerald-700" :
                      status === "QA_FAILED" ? "text-red-400 border-red-800" :
                      status === "OUTDATED" ? "text-amber-400 border-amber-800" : "text-[#8A8A8A] border-[#2A2A2A]";
                    return (
                      <button key={lang} type="button" onClick={() => setActiveLang(lang)}
                        className={"border p-2 text-left " + color} data-testid={"ve-status-" + lang}>
                        <span className="text-[10px] uppercase tracking-[0.16em]">{lang}</span>
                        <div className="text-xs font-semibold mt-1">{status}</div>
                        <div className="text-[9px] opacity-70">rev {editing.translationRevision?.[lang] || 0}/{editing.sourceRevision || 1}</div>
                      </button>
                    );
                  })}
                </div>
                {(editing.qaIssues?.[activeLang] || []).length > 0 && (
                  <div className="mt-3 text-[10px] text-red-400">
                    QA {activeLang.toUpperCase()}: {editing.qaIssues[activeLang].join(" · ")}
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 border border-[#2A2A2A]">
              <div className="flex border-b border-[#2A2A2A]">
                {LANGS.map((l) => (
                  <button key={l} type="button"
                    data-testid={`ve-tab-${l}`}
                    onClick={() => setActiveLang(l)}
                    className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-semibold ${activeLang === l ? "bg-[#C9A55A] text-[#0C0C0C]" : "text-[#F5F1EB]"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="p-4 space-y-3">
                <Field label={`Category label (${activeLang})`} value={editing.categoryLabel?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, categoryLabel: { ...editing.categoryLabel, [activeLang]: v }})} />
                <Field label={`Short description (${activeLang})`} value={editing.descriptionShort?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, descriptionShort: { ...editing.descriptionShort, [activeLang]: v }})} textarea />
                <Field label={`Full description (${activeLang})`} value={editing.descriptionFull?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, descriptionFull: { ...editing.descriptionFull, [activeLang]: v }})} textarea />
                <Field label={`Conditions text (${activeLang})`} value={editing.conditionsText?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, conditionsText: { ...editing.conditionsText, [activeLang]: v }})} textarea />
                <Field label={`H1 (${activeLang})`} value={editing.vehicleH1?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, vehicleH1: { ...editing.vehicleH1, [activeLang]: v }})} testid={`ve-h1-${activeLang}`} />
                <Field label={`Commercial copy (${activeLang})`} value={editing.commercialCopy?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, commercialCopy: { ...editing.commercialCopy, [activeLang]: v }})} textarea testid={`ve-commercial-${activeLang}`} />
                <Field label={`SEO title (${activeLang})`} value={editing.seoTitle?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, seoTitle: { ...editing.seoTitle, [activeLang]: v }})} testid={`ve-seoTitle-${activeLang}`} />
                <Field label={`SEO meta description (${activeLang})`} value={editing.seoDescription?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, seoDescription: { ...editing.seoDescription, [activeLang]: v }})} textarea testid={`ve-seoDesc-${activeLang}`} />
                <Field label={`SEO long copy (${activeLang})`} value={editing.seoDescriptionLong?.[activeLang] || ""}
                  onChange={(v) => setEditing({...editing, seoDescriptionLong: { ...editing.seoDescriptionLong, [activeLang]: v }})} textarea testid={`ve-seoLong-${activeLang}`} />
              </div>
            </div>

            <div className="mt-6 border border-[#2A2A2A] p-4" data-testid="ve-seo-block">
              <div className="text-overline text-[#C9A55A] mb-3">SEO</div>
              <Field
                label="Slug (URL — leave empty to auto-generate from name)"
                value={editing.slug || ""}
                onChange={(v) => setEditing({...editing, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-")})}
                testid="ve-slug"
              />
              <p className="text-[10px] text-[#8A8A8A] mt-1">
                Detail URLs use the same central vehicle and preserve the active locale across all 8 languages.
              </p>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <label className="flex items-center gap-2 text-[#F5F1EB]">
                  <input type="checkbox" checked={editing.seoEnabled !== false}
                    onChange={(e) => setEditing({...editing, seoEnabled: e.target.checked})}
                    data-testid="ve-seoEnabled" />
                  SEO page enabled (uncheck to disable detail page)
                </label>
                <label className="flex items-center gap-2 text-[#F5F1EB]">
                  <input type="checkbox" checked={!!editing.noindex}
                    onChange={(e) => setEditing({...editing, noindex: e.target.checked})}
                    data-testid="ve-noindex" />
                  noindex (hide from search engines)
                </label>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-overline text-[#C9A55A]">FAQs</div>
                  <button type="button" onClick={addFaq} className="text-xs uppercase tracking-[0.18em] text-[#C9A55A] hover:text-[#F5F1EB]" data-testid="ve-faq-add">+ Add FAQ</button>
                </div>
                {(editing.faqs || []).length === 0 && <p className="text-[10px] text-[#8A8A8A]">No FAQs yet. Add at least 2-3 in every published language for richer SEO results.</p>}
                {(editing.faqs || []).map((f, i) => (
                  <div key={f._uid || `faq-${i}`} className="border border-[#2A2A2A] p-3 mb-3" data-testid={`ve-faq-item-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase">FAQ #{i + 1}</div>
                      <button type="button" onClick={() => removeFaq(i)} className="text-[10px] text-[#B8342A] uppercase tracking-[0.18em]" data-testid={`ve-faq-del-${i}`}>Remove</button>
                    </div>
                    <Field label={`Question (${activeLang})`} value={f.q?.[activeLang] || ""} onChange={(v) => setFaq(i, "q", activeLang, v)} testid={`ve-faq-q-${i}-${activeLang}`} />
                    <div className="mt-2">
                      <Field label={`Answer (${activeLang})`} value={f.a?.[activeLang] || ""} onChange={(v) => setFaq(i, "a", activeLang, v)} textarea testid={`ve-faq-a-${i}-${activeLang}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-6 text-sm">
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={!!editing.available} onChange={(e) => setEditing({...editing, available: e.target.checked})} data-testid="ve-available" /> Available</label>
              <label className="flex items-center gap-2 text-[#F5F1EB]"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({...editing, featured: e.target.checked})} data-testid="ve-featured" /> Featured</label>
            </div>

            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 border border-[#2A2A2A] text-[#F5F1EB] uppercase text-xs tracking-[0.18em]">Cancel</button>
              <button onClick={save} className="btn-gold" data-testid="ve-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;

function Field({ label, value, onChange, type = "text", textarea = false, testid, step }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm" />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" />
      )}
    </div>
  );
}
